/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import * as stagesCase from "../../../Stages/StagesCase";
import { STAGE_ACTION_REQUIRED } from "../../../Stages/StagesCase";
import personFields from "../modules/personFields";
import {
  dateFieldDefault,
  emailDefault,
  faxNumberDefault,
  ibanFieldDefault,
  percentageFieldDefault,
  postalCodeDefault,
  textFieldDefault,
} from "../../../validationRules";
import addressFields from "../modules/addressFields";
import { trueFalseDefaultOptions } from "../formDefinitionFunctions";
import InputAdornment from "@mui/material/InputAdornment";
import { IbanInput } from "../../../../components/MaskedInputs/IbanInput";
import _ from "lodash";
import legalForms from "../../../legalForms";
import { objectivesLawFirm } from "../modules/objectiveLawfirmValues";
import { arrayToSelectOptions } from "../../../formServiceFunctions";
import { getCommonContractLawCaseRejectionCategorySelectOptions } from "../contract/contractLawFormHelperService";
import React from "react";
import { UseForm } from "../../../../types/UseForm";
import { BackofficeUser } from "../../../../types/BackofficeUser";
import {
  addProcessParticipant,
  caseInsurance,
  litigationCourt,
  extraJudicialProcedure,
  getAdditionalClientsForm,
  getNextInstanceSection,
  judicialProcedure,
  opponentLawyer,
  opposingLiabilityInsurance,
  processParticipantsOrganizations,
  processParticipantsPeople,
} from "../abstractLitigationCase/fullFormDefinition";
import { RentalContract } from "../../../../types/Products/RentalContract";
import { translate } from "../../../Translations/translatorService";
import { getCourtAppointmentFields } from "../modules/courtAppointmentFields";
import { debtCollectionAgencyFields } from "../modules/debtCollectionFields";

export const fullFormDefinition = (product: RentalContract, currentUser: BackofficeUser) => {
  const contractBaseCreated = {
    isHidden: ({ product }: { product: RentalContract }) => (product.stage || 0) > stagesCase.STAGE_CREATED,
    label: "Allgemeines zum Mietvertrag",
    elements: [...personFields({ path: `${product.productClassName}.client` })],
  };

  const contractBase = {
    isHidden: ({ product }: { product: RentalContract }) => (product.stage || 0) < stagesCase.STAGE_DATA_COMPLETED,
    label: "Allgemeines zum Mietvertrag",
    elements: [
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.contractPartyType`,
        isDisabled: () => true,
        options: () => [{ value: "tenant" }, { value: "landlord" }],
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.caseCategory`,
        isDisabled: ({ product }: { product: RentalContract }) =>
          (product.stage || 0) > STAGE_ACTION_REQUIRED || product.caseCategory === "advisory",
        options: ({ product }: { product: RentalContract }) => {
          return product.caseCategory === "advisory"
            ? [{ value: "enforceClaim" }, { value: "defendClaim" }, { value: "advisory" }]
            : [{ value: "enforceClaim" }, { value: "defendClaim" }];
        },
        alert: {
          onChange:
            "Das Ändern der Position der Mandantschaft (Klägerin/Beklagte) führt dazu, dass sich viele " +
            "Felder ändern und auch Felder verschwinden. Die neuen Felder müssen dann von Ihnen neu " +
            "befüllt werden, damit der Fall weiter bearbeitet werden kann. Möchten Sie die Position " +
            "der Mandantschaft im Verfahren tatsächlich ändern?",
        },
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.affectedPartyType`,
        isDisabled: () => true,
        options: () => [{ value: "customer" }, { value: "coinsured" }, { value: "unknown" }],
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.contractWasTakenOverByOpposingParty`,
        isHidden: ({ product }: { product: RentalContract }) => product.advisoryHandling,
        isDisabled: () => true,
        options: () => trueFalseDefaultOptions(`${product.productClassName}.contractWasTakenOverByOpposingParty`),
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.contractDate`,
        validators: dateFieldDefault,
        isDisabled: ({ product }: { product: RentalContract }) => (product.stage || 0) >= stagesCase.STAGE_LAWSUIT,
        isHidden: ({ product }: { product: RentalContract }) => product.advisoryHandling,
      },
      ...personFields({ path: `${product.productClassName}.client` }),
      ...addressFields({
        path: `${product.productClassName}.client.residenceAddress`,
        isHidden: ({ product }: { product: RentalContract }) => product.advisoryHandling,
      }),
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.subjectOfDispute`,
        translationPath: `${product.productClassName}.subjectOfDispute.${product.caseCategory}`,
        isDisabled: ({ product }: { product: RentalContract }) => (product.stage || 0) >= stagesCase.STAGE_CHECKED,
        isHidden: ({ product }: { product: RentalContract }) => product.advisoryHandling,
        options: () => [{ value: "money" }, { value: "goods" }, { value: "moneyAndGoods" }, { value: "other" }],
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.clientHasAlreadyClaimed`,
        isDisabled: ({ product }: { product: RentalContract }) => (product.stage || 0) >= stagesCase.STAGE_CHECKED,
        isHidden: ({ product }: { product: RentalContract }) => product.caseCategory !== "enforceClaim",
        options: () => trueFalseDefaultOptions(`${product.productClassName}.clientHasAlreadyClaimed`),
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.claimedDateClient`,
        validators: dateFieldDefault,
        isHidden: ({ product }: { product: RentalContract }) => product.caseCategory !== "enforceClaim",
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.claimedDeadlineClient`,
        validators: dateFieldDefault,
        isHidden: ({ product }: { product: RentalContract }) =>
          product.caseCategory !== "enforceClaim" || !product.clientHasAlreadyClaimed,
      },
      {
        type: "ValidatorNumberField",
        path: `${product.productClassName}.claimedAmountClient`,
        isHidden: ({ product }: { product: RentalContract }) => product.subjectOfDispute !== "money",
        numberType: "float",
        fieldType: "money",
      },
      {
        type: "ValidatorNumberField",
        path: `${product.productClassName}.overdueFees`,
        isHidden: ({ product }: { product: RentalContract }) =>
          product.subjectOfDispute !== "money" ||
          (product.caseCategory !== "defendClaim" && !product.clientHasAlreadyClaimed),
        fieldType: "money",
        numberType: "float",
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.client.bankData.iban`,
        validators: ibanFieldDefault,
        additionalProps: {
          InputProps: {
            startAdornment: <InputAdornment position="start">DE</InputAdornment>,
            inputComponent: IbanInput,
          },
        },
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.defenseProcessStatus`,
        isDisabled: ({ product }: { product: RentalContract }) =>
          (product.stage || 0) >= stagesCase.STAGE_DATA_COMPLETED_LAWFIRM,
        isHidden: ({ product }: { product: RentalContract }) => product.caseCategory !== "defendClaim",
        options: () => [
          { value: "reminder" },
          { value: "debtCollection" },
          { value: "paymentOrder" },
          { value: "enforcementOrder" },
          { value: "lawsuit" },
          { value: "none" },
        ],
      },
    ],
  };

  const contractDetails = {
    isHidden: ({ product }: { product: RentalContract }) =>
      (product.stage || 0) < stagesCase.STAGE_DATA_COMPLETED || product.caseCategory === "advisory",
    label: "Details zum Anliegen",
    elements: [
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.reasonForDispute`,
        isDisabled: ({ product }: { product: RentalContract }) => (product.stage || 0) >= stagesCase.STAGE_CHECKED,
        options: () => [
          { value: "landlordHasCancelled" },
          { value: "housingDeficiencies" },
          { value: "rentIncrease" },
          { value: "utilitiesStatement" },
          { value: "deposit" },
          { value: "illegalContractClause" },
          { value: "rentReduction" },
          { value: "rentLate" },
          { value: "terminationRejection" },
          { value: "depositPayout" },
          { value: "other" },
        ],
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.housingDeficienciesDescriptionClient`,
        isDisabled: () => true,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          !(values.rentalContract.reasonForDispute === "housingDeficiencies"),
        additionalProps: {
          multiline: true,
          fullWidth: true,
        },
      },
      {
        type: "ValidatorNumberField",
        path: `${product.productClassName}.rentReductionPercentage`,
        validators: percentageFieldDefault,
        fieldType: "percentage",
        numberType: "float",
        isHidden: ({ product }: { product: RentalContract }) => product.reasonForDispute !== "housingDeficiencies",
      },
      {
        type: "ValidatorNumberField",
        path: `${product.productClassName}.previousBasicRent`,
        fieldType: "money",
        numberType: "float",
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          !["rentIncrease", "housingDeficiencies"].includes(values.rentalContract.reasonForDispute),
        isDisabled: ({ product }: { product: RentalContract }) => (product.stage || 0) >= stagesCase.STAGE_CHECKED,
      },
      {
        type: "ValidatorNumberField",
        path: `${product.productClassName}.previousRentIncludingHeating`,
        fieldType: "money",
        numberType: "float",
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.rentalContract.reasonForDispute !== "rentIncrease",
        isDisabled: ({ product }: { product: RentalContract }) => (product.stage || 0) >= stagesCase.STAGE_CHECKED,
      },
      {
        type: "ValidatorNumberField",
        path: `${product.productClassName}.futureBasicRent`,
        fieldType: "money",
        numberType: "float",
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.rentalContract.reasonForDispute !== "rentIncrease",
        isDisabled: ({ product }: { product: RentalContract }) => (product.stage || 0) >= stagesCase.STAGE_CHECKED,
      },
      {
        type: "ValidatorNumberField",
        path: `${product.productClassName}.futureRentIncludingHeating`,
        fieldType: "money",
        numberType: "float",
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.rentalContract.reasonForDispute !== "rentIncrease",
        isDisabled: ({ product }: { product: RentalContract }) => (product.stage || 0) >= stagesCase.STAGE_CHECKED,
      },
      {
        type: "ValidatorNumberField",
        path: `${product.productClassName}.depositAmount`,
        fieldType: "money",
        numberType: "float",
        isHidden: ({ values }: { values: UseForm["values"] }) => values.rentalContract.reasonForDispute !== "deposit",
        isDisabled: ({ product }: { product: RentalContract }) => (product.stage || 0) >= stagesCase.STAGE_CHECKED,
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.cancellationReceivedDate`,
        validators: dateFieldDefault,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.rentalContract.reasonForDispute !== "landlordHasCancelled",
        isDisabled: ({ product }: { product: RentalContract }) => (product.stage || 0) >= stagesCase.STAGE_CHECKED,
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.rentalContractStartDate`,
        validators: dateFieldDefault,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.rentalContract.reasonForDispute !== "utilitiesStatement" &&
          values.rentalContract.reasonForDispute !== "deposit" &&
          values.rentalContract.reasonForDispute !== "landlordHasCancelled",
        isDisabled: ({ product }: { product: RentalContract }) => (product.stage || 0) >= stagesCase.STAGE_CHECKED,
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.rentalContractEndDate`,
        validators: dateFieldDefault,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.rentalContract.reasonForDispute !== "utilitiesStatement" &&
          values.rentalContract.reasonForDispute !== "deposit" &&
          values.rentalContract.reasonForDispute !== "landlordHasCancelled",
        isDisabled: ({ product }: { product: RentalContract }) => (product.stage || 0) >= stagesCase.STAGE_CHECKED,
      },
    ],
  };

  const clientLegalRepresentative = {
    label: "Gesetzlicher Vertreter",
    isHidden: ({ product }: { product: RentalContract }) => !product.clientWasMinorAtContractDate,
    elements: [
      ...personFields({ path: `${product.productClassName}.clientLegalRepresentative` }),
      ...addressFields({ path: `${product.productClassName}.clientLegalRepresentative.residenceAddress` }),
    ],
  };

  const contractPropertyAddress = {
    label: "Anschrift der Mietsache",
    elements: [...addressFields({ path: `${product.productClassName}.propertyAddress`, hideAddressAddition: false })],
    isHidden: ({ product }: { product: RentalContract }) => product.advisoryHandling,
  };

  const propertyManager = {
    label: "Hausverwaltung",
    isHidden: ({ product }: { product: RentalContract }) => product.advisoryHandling,
    elements: [
      {
        type: "ValidatorTextField",
        path: "rentalContract.propertyManager.organization.legalName",
        validators: textFieldDefault,
      },
      {
        type: "ValidatorTextField",
        path: "rentalContract.propertyManager.organization.address.streetAddress",
        validators: textFieldDefault,
      },
      {
        type: "ValidatorTextField",
        path: "rentalContract.propertyManager.organization.address.postalCode",
        validators: [...textFieldDefault, postalCodeDefault],
      },
      {
        type: "ValidatorTextField",
        path: "rentalContract.propertyManager.organization.address.addressLocality",
        validators: textFieldDefault,
      },
      {
        type: "ValidatorTextField",
        path: "rentalContract.propertyManager.organization.mainEmail.email",
        validators: textFieldDefault,
      },
      {
        type: "ValidatorTextField",
        path: "rentalContract.propertyManager.organization.telephone",
        validators: textFieldDefault,
      },
      {
        type: "ValidatorTextField",
        path: "rentalContract.propertyManager.organization.fax",
        validators: faxNumberDefault,
        additionalProps: {
          InputProps: {
            startAdornment: <InputAdornment position="start">+49</InputAdornment>,
          },
        },
      },
      {
        type: "ValidatorTextField",
        path: "rentalContract.propertyManagerReference",
        validators: textFieldDefault,
      },
    ],
  };

  const contractPartner = {
    isHidden: ({ product }: { product: RentalContract }) =>
      (product.stage || 0) < stagesCase.STAGE_DATA_COMPLETED || product.caseCategory === "advisory",
    label: `Gegenseite - ${product.contractPartyType === "landlord" ? "Mieter" : "Vermieter"}`,
    elements: [
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.opposingPartyType`,
        isDisabled: ({ product }: { product: RentalContract }) => (product.stage || 0) >= stagesCase.STAGE_COMPLETED,
        options: () => [{ value: "opposingPartyLegalEntity" }, { value: "opposingPartyPerson" }],
        additionalProps: {
          fullWidth: true,
        },
      },
      {
        type: "ValidatorTextField",
        path: "rentalContract.contractualPartnerLegalEntity.organization.legalName",
        isDisabled: ({ product }: { product: UseForm["values"] }) => (product.stage || 0) >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.rentalContract.opposingPartyType !== "opposingPartyLegalEntity",
        validators: textFieldDefault,
      },
      {
        type: "ValidatorSelect",
        path: "rentalContract.contractualPartnerLegalEntity.organization.legalForm",
        isDisabled: ({ product }: { product: RentalContract }) => (product.stage || 0) >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.rentalContract.opposingPartyType !== "opposingPartyLegalEntity",
        options: () => [
          { value: "__null__", label: "Keine Gesellschaftsform" },
          ..._.map(legalForms, (legalForm) => {
            return {
              value: legalForm.label,
              label: legalForm.label,
            };
          }),
        ],
      },
      {
        type: "ValidatorTextField",
        path: "rentalContract.contractualPartnerLegalEntity.organization.address.streetAddress",
        isDisabled: ({ product }: { product: RentalContract }) => (product.stage || 0) >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.rentalContract.opposingPartyType !== "opposingPartyLegalEntity",
        validators: textFieldDefault,
      },
      {
        type: "ValidatorTextField",
        path: "rentalContract.contractualPartnerLegalEntity.organization.address.postalCode",
        isDisabled: ({ product }: { product: RentalContract }) => (product.stage || 0) >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.rentalContract.opposingPartyType !== "opposingPartyLegalEntity",
        validators: [...textFieldDefault, postalCodeDefault],
      },
      {
        type: "ValidatorTextField",
        path: "rentalContract.contractualPartnerLegalEntity.organization.address.addressLocality",
        isDisabled: ({ product }: { product: RentalContract }) => (product.stage || 0) >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.rentalContract.opposingPartyType !== "opposingPartyLegalEntity",
        validators: textFieldDefault,
      },
      {
        type: "ValidatorTextField",
        path: "rentalContract.contractualPartnerLegalEntity.organization.address.postOfficeBoxNumber",
        isDisabled: ({ product }: { product: RentalContract }) => (product.stage || 0) >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.rentalContract.opposingPartyType !== "opposingPartyLegalEntity",
        validators: textFieldDefault,
      },
      {
        type: "ValidatorTextField",
        path: "rentalContract.contractualPartnerLegalEntity.organization.address.postOfficeBoxPostalCode",
        isDisabled: ({ product }: { product: RentalContract }) => (product.stage || 0) >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.rentalContract.opposingPartyType !== "opposingPartyLegalEntity",
        validators: [...textFieldDefault, postalCodeDefault],
      },
      {
        type: "ValidatorTextField",
        path: "rentalContract.contractualPartnerLegalEntity.organization.address.postOfficeBoxAddressLocality",
        isDisabled: ({ product }: { product: RentalContract }) => (product.stage || 0) >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.rentalContract.opposingPartyType !== "opposingPartyLegalEntity",
        validators: textFieldDefault,
      },
      {
        type: "ValidatorTextField",
        path: "rentalContract.contractualPartnerLegalEntity.organization.mainEmail.email",
        isDisabled: ({ product }: { product: RentalContract }) => (product.stage || 0) >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.rentalContract.opposingPartyType !== "opposingPartyLegalEntity",
        validators: textFieldDefault,
      },
      {
        type: "ValidatorTextField",
        path: "rentalContract.contractualPartnerLegalEntity.organization.telephone",
        isDisabled: ({ product }: { product: RentalContract }) => (product.stage || 0) >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.rentalContract.opposingPartyType !== "opposingPartyLegalEntity",
        validators: textFieldDefault,
      },
      {
        type: "ValidatorTextField",
        path: "rentalContract.contractualPartnerLegalEntity.organization.fax",
        isDisabled: ({ product }: { product: RentalContract }) => (product.stage || 0) >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.rentalContract.opposingPartyType !== "opposingPartyLegalEntity",
        validators: faxNumberDefault,
        additionalProps: {
          InputProps: {
            startAdornment: <InputAdornment position="start">+49</InputAdornment>,
          },
        },
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyReference`,
        helperText: translate(`${product.productClassName}.opposingPartyReference.helperText`),
        isDisabled: ({ product }: { product: RentalContract }) => product.stage < stagesCase.STAGE_DATA_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.rentalContract.opposingPartyType !== "opposingPartyLegalEntity",
      },
      {
        type: "ContractRepresentatives",
        path: "rentalContract.contractualPartnerLegalEntity.organization.additionalRepresentatives",
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.opposingPartyType === "opposingPartyLegalEntity",
        additionalProps: {
          fullWidth: true,
          gridSpacing: 4,
          gridSize: 4,
          buttonFullWidth: false,
        },
      },
      ...personFields({
        path: `${product.productClassName}.contractualPartnerPerson`,
        allowNoGender: true,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.rentalContract.opposingPartyType !== "opposingPartyPerson",
        isDisabled: ({ product }: { product: RentalContract; values: UseForm["values"] }) =>
          (product.stage || 0) >= stagesCase.STAGE_COMPLETED,
      }),
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.contractualPartnerPerson.email`,
        validators: emailDefault,
        isDisabled: ({ product }: { product: RentalContract }) => (product.stage || 0) >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.rentalContract.opposingPartyType !== "opposingPartyPerson",
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.contractualPartnerPerson.telephone`,
        isDisabled: ({ product }: { product: RentalContract }) => (product.stage || 0) >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.rentalContract.opposingPartyType !== "opposingPartyPerson",
      },
      ...addressFields({
        path: `${product.productClassName}.contractualPartnerPerson.residenceAddress`,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.rentalContract.opposingPartyType !== "opposingPartyPerson",
        isDisabled: ({ product }: { product: RentalContract }) => (product.stage || 0) >= stagesCase.STAGE_COMPLETED,
      }),
    ],
  };

  const contractDescription = {
    isHidden: ({ product }: { product: RentalContract }) => (product.stage || 0) < stagesCase.STAGE_DATA_COMPLETED,
    label: "Beschreibung der Situation",
    elements: [
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.personalTextMessage`,
        isDisabled: () => true,
        additionalProps: {
          multiline: true,
          fullWidth: true,
        },
      },
    ],
  };

  return {
    product: {
      sections: [
        contractBaseCreated,
        contractBase,
        contractPropertyAddress,
        contractDetails,
        contractDescription,
        getAdditionalClientsForm(product),
      ],
    },
    additional: {
      sections: [
        {
          label: "Allgemeines",
          elements: [
            {
              type: "ValidatorTextField",
              path: `${product.productClassName}.reference`,
              isDisabled: () => true,
            },
            {
              type: "ValidatorTextField",
              path: `${product.productClassName}.powerOfAttorney`,
              isHidden: ({ product }: { product: RentalContract }) =>
                (product.stage || 0) < stagesCase.STAGE_DATA_COMPLETED || product.caseCategory === "advisory",
              isDisabled: () => true,
            },
            {
              type: "ValidatorSelect",
              path: `${product.productClassName}.objectiveLawFirm`,
              isDisabled: ({ product }: { product: RentalContract }) =>
                (product.stage || 0) >= stagesCase.STAGE_CHECKED,
              isHidden: ({ product }: { product: RentalContract }) => product.advisoryHandling,
              options: ({ product }: { product: RentalContract }) => {
                if (_.isEmpty(product.possibleObjectives)) {
                  return objectivesLawFirm;
                } else {
                  return arrayToSelectOptions(product.possibleObjectives);
                }
              },
            },
            {
              type: "ValidatorSelect",
              path: `${product.productClassName}.objectiveLawFirmSecondary`,
              isDisabled: ({ product }: { product: RentalContract }) =>
                (product.stage || 0) >= stagesCase.STAGE_CHECKED,
              isHidden: ({ product }: { product: RentalContract }) => product.advisoryHandling,
              options: ({ product }: { product: RentalContract }) => {
                if (_.isEmpty(product.possibleObjectives)) {
                  return objectivesLawFirm;
                } else {
                  return arrayToSelectOptions(product.possibleObjectives);
                }
              },
            },
            {
              type: "ValidatorSelect",
              path: `${product.productClassName}.objectiveLawFirmTertiary`,
              isDisabled: ({ product }: { product: RentalContract }) =>
                (product.stage || 0) >= stagesCase.STAGE_CHECKED,
              isHidden: ({ product }: { product: RentalContract }) => product.advisoryHandling,
              options: ({ product }: { product: RentalContract }) => {
                if (_.isEmpty(product.possibleObjectives)) {
                  return objectivesLawFirm;
                } else {
                  return arrayToSelectOptions(product.possibleObjectives);
                }
              },
            },
            {
              type: "ValidatorSelect",
              path: `${product.productClassName}.caseCategoryAssessment`,
              isHidden: ({ product }: { product: RentalContract }) =>
                (product.stage || 0) < stagesCase.STAGE_DATA_COMPLETED || product.caseCategory === "advisory",
              isDisabled: ({ product }: { product: RentalContract }) =>
                (product.stage || 0) > stagesCase.STAGE_WAITING_FOR_FEEDBACK,
              options: () => [{ value: "confirm" }, { value: "reject" }],
            },
          ],
        },
        extraJudicialProcedure<RentalContract>(
          product,
          currentUser,
          [
            {
              type: "ValidatorTextField",
              path: `${product.productClassName}.additionalIssueDescriptionText`,
              isDisabled: ({ product }: { product: RentalContract }) =>
                (product.stage || 0) >= stagesCase.STAGE_CHECKED,
              isHidden: ({ product }: { product: RentalContract }) =>
                product.advisoryHandling ||
                product.isLawsuitHandling ||
                ((product.stage || 0) >= stagesCase.STAGE_CHECKED && !product.additionalIssueDescriptionText),
              additionalProps: {
                multiline: true,
                fullWidth: true,
              },
            },
            {
              type: "ValidatorTextField",
              path: `${product.productClassName}.additionalClaimText`,
              isDisabled: ({ product }: { product: RentalContract }) =>
                (product.stage || 0) >= stagesCase.STAGE_CHECKED,
              isHidden: ({ product }: { product: RentalContract }) =>
                product.advisoryHandling ||
                product.isLawsuitHandling ||
                ((product.stage || 0) >= stagesCase.STAGE_CHECKED && !product.additionalClaimText),
              additionalProps: {
                multiline: true,
                fullWidth: true,
              },
            },
            {
              type: "ValidatorTextField",
              path: `${product.productClassName}.housingDeficienciesDescriptionLawFirm`,
              isDisabled: ({ product }: { product: RentalContract }) =>
                (product.stage || 0) >= stagesCase.STAGE_CHECKED,
              isHidden: ({ product, values }: { product: RentalContract; values: UseForm["values"] }) =>
                product.caseCategory !== "enforceClaim" ||
                !(values.rentalContract.reasonForDispute === "housingDeficiencies"),
              additionalProps: {
                multiline: true,
                fullWidth: true,
              },
            },
          ],
          getCommonContractLawCaseRejectionCategorySelectOptions,
          () => {
            return [
              { value: "agreementDone" },
              { value: "insufficientEvidence" },
              { value: "insufficientChancesOfSuccess" },
              { value: "costBenefitConsiderations" },
              { value: "lawsuit" },
              { value: "individualText" },
            ];
          },
        ),
        judicialProcedure(product),
        {
          label: product.courtAppointments.length === 1 ? "Gerichtstermin" : "Gerichtstermine",
          isHidden: ({ product }: { product: RentalContract }) => product.courtAppointments.length === 0,
          elements: getCourtAppointmentFields(product),
        },
        getNextInstanceSection(product),
      ],
    },
    processParticipants: {
      asAccordion: true,
      sections: [
        contractPartner,
        caseInsurance(product),
        clientLegalRepresentative,
        debtCollectionAgencyFields(product),
        opponentLawyer(product),
        propertyManager,
        {
          ...opposingLiabilityInsurance(product),
          isHidden: ({ product }: { product: RentalContract }) => product.advisoryHandling,
        },
      ],
    },
    additionalProcessParticipants: {
      isHidden: () => product.advisoryHandling,
      sections: [
        addProcessParticipant(),
        processParticipantsPeople(product),
        processParticipantsOrganizations(product),
      ],
      withoutSubmit: true,
    },
    court: {
      isHidden: () => product.advisoryHandling,
      sections: [
        litigationCourt(product, ["Amtsgericht", "Arbeitsgericht", "Landgericht", "Mahngericht", "Oberlandesgericht"]),
      ],
    },
  };
};
